import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Package, PoundSterling } from 'lucide-react';

export default function AddOnsManager({ services = [] }) {
  const [addons, setAddons] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [form, setForm] = useState({ name: '', price: '', service_ids: [] });

  useEffect(() => { fetchAddons(); }, []);

  const fetchAddons = async () => {
    try {
      const res = await api.get('/addons');
      setAddons(res.data || []);
    } catch (err) { console.error('Failed to load addons:', err); }
  };

  const openCreate = () => {
    setEditingAddon(null);
    setForm({ name: '', price: '', service_ids: [] });
    setDialogOpen(true);
  };

  const openEdit = (addon) => {
    setEditingAddon(addon);
    setForm({ name: addon.name, price: String(addon.price), service_ids: addon.service_ids || [] });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.price) {
      toast.error('Name and price are required');
      return;
    }
    try {
      const payload = { name: form.name.trim(), price: parseFloat(form.price), service_ids: form.service_ids };
      if (editingAddon) {
        const res = await api.put(`/addons/${editingAddon.addon_id}`, payload);
        setAddons(prev => prev.map(a => a.addon_id === editingAddon.addon_id ? res.data : a));
        toast.success('Add-on updated');
      } else {
        const res = await api.post('/addons', payload);
        setAddons(prev => [...prev, res.data]);
        toast.success('Add-on created');
      }
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (addonId) => {
    try {
      await api.delete(`/addons/${addonId}`);
      setAddons(prev => prev.filter(a => a.addon_id !== addonId));
      toast.success('Add-on deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const toggleService = (serviceId) => {
    setForm(prev => ({
      ...prev,
      service_ids: prev.service_ids.includes(serviceId)
        ? prev.service_ids.filter(id => id !== serviceId)
        : [...prev.service_ids, serviceId]
    }));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
          <PoundSterling className="h-5 w-5 text-[#D4AF37]" />
          Add-ons
        </h3>
        <Button onClick={openCreate} className="btn-primary gap-1" size="sm" data-testid="create-addon-btn">
          <Plus className="h-4 w-4" /> New Add-on
        </Button>
      </div>

      {addons.length === 0 ? (
        <div className="text-center py-6 text-stone-500 text-sm">
          <p>No add-ons yet</p>
          <p className="text-xs text-stone-400 mt-1">Create extras like "Extra Length +£15" or "Call-out Fee +£20"</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="addons-list">
          {addons.map(addon => (
            <div key={addon.addon_id} className="flex items-center justify-between p-3 bg-[#F9F6F0] rounded-lg" data-testid={`addon-${addon.addon_id}`}>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-[#1A1A1A] text-sm">{addon.name}</span>
                <span className="text-[#D4AF37] font-semibold text-sm ml-2">+£{addon.price.toFixed(2)}</span>
                {addon.service_ids?.length > 0 && (
                  <p className="text-xs text-stone-400 mt-0.5">
                    Assigned to {addon.service_ids.length} service{addon.service_ids.length > 1 ? 's' : ''}
                  </p>
                )}
                {(!addon.service_ids || addon.service_ids.length === 0) && (
                  <p className="text-xs text-stone-400 mt-0.5">Available on all services</p>
                )}
              </div>
              <div className="flex gap-1 ml-2">
                <button onClick={() => openEdit(addon)} className="p-1.5 hover:bg-white rounded transition-colors">
                  <Edit className="h-3.5 w-3.5 text-stone-500" />
                </button>
                <button onClick={() => handleDelete(addon.addon_id)} className="p-1.5 hover:bg-red-50 rounded transition-colors">
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingAddon ? 'Edit Add-on' : 'New Add-on'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="addon-name">Name *</Label>
              <Input id="addon-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder='e.g. "Extra Length"' className="input-zuriya" data-testid="addon-name-input" />
            </div>
            <div>
              <Label htmlFor="addon-price">Price (£) *</Label>
              <Input id="addon-price" type="number" min="0" step="0.01" value={form.price}
                onChange={e => setForm({ ...form, price: e.target.value })}
                placeholder="15.00" className="input-zuriya max-w-[160px]" data-testid="addon-price-input" />
            </div>
            {services.length > 0 && (
              <div>
                <Label className="mb-2 block">Assign to Services (optional)</Label>
                <p className="text-xs text-stone-400 mb-2">Leave unchecked to make available on all services</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {services.map(svc => (
                    <label key={svc.service_id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={form.service_ids.includes(svc.service_id)}
                        onCheckedChange={() => toggleService(svc.service_id)}
                      />
                      <span>{svc.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <Button onClick={handleSave} className="btn-primary w-full" data-testid="save-addon-btn">
              {editingAddon ? 'Update Add-on' : 'Create Add-on'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
